import { getAccessToken } from './interceptor';

export class NetworkError extends Error {
  // Server-shaped error body (the standard `{ error, message }` JSON
  // most /v1 handlers return on 4xx/5xx). Populated when the response
  // had a JSON content-type and a non-2xx status; left undefined when
  // the failure happened before we could read the body (CORS, abort,
  // network, parse). Callers that want to surface the server's
  // human-readable message in the UI should prefer `serverMessage`.
  readonly serverError?: string;
  readonly serverMessage?: string;

  constructor(
    public readonly kind: 'http' | 'cors' | 'timeout' | 'aborted' | 'parse',
    public readonly status?: number,
    msg?: string,
    body?: { error?: string; message?: string },
  ) {
    super(msg ?? `network: ${kind}${status != null ? ` ${status}` : ''}`);
    this.name = 'NetworkError';
    if (body?.error) this.serverError = body.error;
    if (body?.message) this.serverMessage = body.message;
  }
}

// Read the base URL on every request rather than once at module load.
// This lets a developer point their browser tab at a different proxy
// without refreshing the bundle, lets tests override the value with
// `vi.stubEnv('VITE_API_BASE_URL', ...)`, and avoids freezing whatever
// happened to be in `import.meta.env` when this file was first imported.
function getBaseUrl(): string {
  // Default: relative paths so the Vite dev server proxies `/v1/*` to
  // the API host configured in vite.config.ts (`DEFAULT_API_PROXY_TARGET`),
  // avoiding CORS preflight failures against the upstream. Set
  // `VITE_API_BASE_URL` in production to a fully-qualified URL.
  // `??` (nullish coalescing) so an explicit empty string still means
  // "use relative paths".
  return import.meta.env.VITE_API_BASE_URL ?? '';
}
function getToken(): string | null {
  return getAccessToken();
}

interface RequestOptions {
  signal?: AbortSignal;
  /**
   * Per-request header overrides. Merged over the auto-managed
   * defaults (Authorization, auto-Content-Type) so a caller can
   * e.g. pin `Content-Type: application/cbor` for the MLS message
   * envelope without re-implementing the rest of the request stack.
   */
  headers?: Record<string, string>;
}

interface PostRequestOptions extends RequestOptions {
  idempotencyKey?: string;
}

/**
 * `true` when the body is a binary type the fetch API can send
 * directly (no JSON.stringify, no auto Content-Type). The MLS
 * transport sends application/cbor ArrayBuffers, so we MUST NOT
 * JSON-encode those.
 */
function isBinaryBody(body: unknown): body is BodyInit {
  return (
    body instanceof ArrayBuffer ||
    body instanceof Uint8Array ||
    body instanceof Blob ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof ReadableStream ||
    typeof body === 'string'
  );
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts: PostRequestOptions = {},
): Promise<T> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${path}`;
  const headers: Record<string, string> = {};

  const token = getToken();
  if (token != null) {
    headers.Authorization = `Bearer ${token}`;
  }

  const binary = body != null && isBinaryBody(body);
  if (body != null && !binary) {
    // Default for object bodies: JSON. The caller can still override
    // via opts.headers below.
    headers['Content-Type'] = 'application/json';
  }

  if ('idempotencyKey' in opts && opts.idempotencyKey != null) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }

  // Caller-supplied headers win. This is the hook the MLS transport
  // uses to pin Content-Type: application/cbor.
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) {
      headers[k] = v;
    }
  }

  let response: Response;

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (opts.signal != null) {
    fetchOptions.signal = opts.signal;
  }

  if (body != null) {
    fetchOptions.body = binary ? (body as BodyInit) : JSON.stringify(body);
  }

  try {
    response = await fetch(url, fetchOptions);
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new NetworkError('aborted');
      }
      if (
        error.message.includes('CORS') ||
        error.message.includes('NetworkError') ||
        error.message.includes('Failed to fetch')
      ) {
        throw new NetworkError('cors');
      }
    }
    throw new NetworkError('http');
  }

  // Order matters: check transport first, then envelope, then payload.
  // 1. Server-shaped HTTP errors (4xx/5xx) are real responses — classify
  //    as `http` even if BASE_URL was overridden (e.g. by `.env.local`).
  // 2. Origin mismatch is a redirect/proxy safety net, only meaningful
  //    when the response was actually accepted as 2xx. URL parse errors
  //    are tolerated so a malformed `response.url` can't poison the
  //    classification.
  // 3. JSON parse failure on an otherwise-good response → `parse`.
  if (!response.ok) {
    // Best-effort: peel the server's `{ error, message }` body so the
    // UI can surface the human-readable string instead of "http 410".
    // Only attempt the parse on JSON responses — CBOR / empty / opaque
    // bodies fall through to a kindless NetworkError.
    let body: { error?: string; message?: string } | undefined;
    const ctype = response.headers.get('content-type') ?? '';
    if (ctype.includes('application/json')) {
      try {
        const parsed = (await response.json()) as { error?: string; message?: string };
        if (parsed && typeof parsed === 'object') body = parsed;
      } catch {
        // ignore — leave body undefined
      }
    }
    throw new NetworkError('http', response.status, undefined, body);
  }

  try {
    const responseOrigin = new URL(response.url).origin;
    const baseOrigin = new URL(baseUrl).origin;
    if (responseOrigin !== baseOrigin) {
      throw new NetworkError('cors');
    }
  } catch (error) {
    if (error instanceof NetworkError) throw error;
    // URL parse failure — tolerate; the response itself is well-formed
    // enough that fetch returned 2xx.
  }

  // If the caller asked for a non-JSON response via Accept, hand
  // them the raw ArrayBuffer instead of calling response.json().
  // The MLS sync path (`/v1/sync`) returns CBOR and the existing
  // transport casts the result as `ArrayBuffer`, so without this
  // branch it'd hit the parse path below and 5xx the call.
  const accept = (opts.headers?.Accept ?? opts.headers?.accept ?? '').toLowerCase();
  const wantsBinary =
    accept.includes('application/cbor') || accept.includes('application/octet-stream');
  if (wantsBinary) {
    try {
      return (await response.arrayBuffer()) as unknown as T;
    } catch {
      throw new NetworkError('parse');
    }
  }

  // Empty 204 bodies (e.g. POST /v1/contacts) are valid responses
  // but `.json()` throws on the empty stream. Guard so callers that
  // type the response as `void` don't blow up here.
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new NetworkError('parse');
  }
}

export async function get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return request<T>('GET', path, undefined, opts);
}

export async function post<T>(
  path: string,
  body: unknown,
  opts: PostRequestOptions = {},
): Promise<T> {
  return request<T>('POST', path, body, opts);
}

export async function put<T>(path: string, body: unknown, opts: RequestOptions = {}): Promise<T> {
  return request<T>('PUT', path, body, opts);
}

export async function patch<T>(path: string, body: unknown, opts: RequestOptions = {}): Promise<T> {
  return request<T>('PATCH', path, body, opts);
}

export async function del<T>(
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  return request<T>('DELETE', path, body, opts);
}

export const api = {
  get,
  post,
  put,
  patch,
  delete: del,
};
